// src/components/dashboard/DashboardProfile.tsx
//
// "Your Profile" — single nav entry, two tabs, per the resolved IA
// decision: capabilities + engagement requirements are settings-shaped
// (grouped as "How You Work"), offerings are content-shaped, growable,
// and get their own tab with an obvious add affordance ("Services &
// Pricing"). One page, one nav decision, lower friction than two
// separate destinations — and avoids the earlier flagged ambiguity of
// two similarly-worded sidebar items with nothing to disambiguate them.
//
// MIGRATED — local FDW_BASE / dashboardToken / getDashboardToken /
// authedFetch block removed in favor of the shared dashboardApi client.
// Five call sites updated across OfferingsTab and HowYouWorkTab:
// offerings, offerings/save, capabilities, engagement-requirements,
// engagement-requirements/save — all now relative paths through /api.

import React, { useState, useEffect } from 'react';
import { authedFetch } from '../../services/dashboardApi';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { PlusIcon, Loader2Icon } from 'lucide-react';
import { SearchConsoleCard } from './SearchConsoleCard';

interface Offering {
  id: string;
  name: string;
  description: string;
  type: 'engagement' | 'project' | 'retainer' | 'one-off';
  priceModel: {
    kind: 'fixed' | 'hourly' | 'range';
    amountCents: number | null;
    minCents: number | null;
    maxCents: number | null;
  };
  active: boolean;
}

interface Capability {
  id: string;
  label: string;
  current_state: 'ACTIVE' | 'PENDING';
  pattern?: string;
  fits_if?: string;
}

interface EngagementRequirements {
  applicable: boolean;
  minimumProjectValueCents: number | null;
  requiresWrittenBrief: boolean;
  discoveryCallRequired: boolean;
  minimumTimelineDays: number | null;
}

function formatPrice(o: Offering): string {
  const { kind, amountCents, minCents, maxCents } = o.priceModel;
  const fmt = (c: number | null) => (c != null ? `$${(c / 100).toLocaleString()}` : '—');
  if (kind === 'fixed') return fmt(amountCents);
  if (kind === 'hourly') return `${fmt(amountCents)}/hr`;
  return `${fmt(minCents)}–${fmt(maxCents)}`;
}

// ---- Services & Pricing tab -----------------------------------------

function OfferingEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: Partial<Offering>;
  onSave: (o: Omit<Offering, 'id'> & { id?: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name ?? '');
  const [description, setDescription] = useState(initial.description ?? '');
  const [type, setType] = useState<Offering['type']>(initial.type ?? 'engagement');
  const [priceKind, setPriceKind] = useState(initial.priceModel?.kind ?? 'fixed');
  const [amount, setAmount] = useState(
    initial.priceModel?.amountCents != null ? String(initial.priceModel.amountCents / 100) : ''
  );
  const [min, setMin] = useState(
    initial.priceModel?.minCents != null ? String(initial.priceModel.minCents / 100) : ''
  );
  const [max, setMax] = useState(
    initial.priceModel?.maxCents != null ? String(initial.priceModel.maxCents / 100) : ''
  );
  const [active, setActive] = useState(initial.active ?? true);
  const [isSaving, setIsSaving] = useState(false);

  const toCents = (v: string) => (v.trim() ? Math.round(parseFloat(v) * 100) : null);

  const handleSave = async () => {
    if (!name.trim() || !description.trim()) return;
    setIsSaving(true);
    try {
      await onSave({
        id: initial.id,
        name: name.trim(),
        description: description.trim(),
        type,
        priceModel: {
          kind: priceKind,
          amountCents: priceKind !== 'range' ? toCents(amount) : null,
          minCents: priceKind === 'range' ? toCents(min) : null,
          maxCents: priceKind === 'range' ? toCents(max) : null,
        },
        active,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-brand-border bg-brand-white p-4">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Offering name — e.g. SEO Strategy Engagement"
        className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Short description — what's included, who it's for"
        rows={2}
        className="w-full resize-none rounded-lg border border-brand-border px-3 py-2 text-sm"
      />
      <div className="flex flex-wrap gap-3">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as Offering['type'])}
          className="rounded-lg border border-brand-border px-2 py-1.5 text-sm"
        >
          <option value="engagement">Engagement</option>
          <option value="project">Project</option>
          <option value="retainer">Retainer</option>
          <option value="one-off">One-off</option>
        </select>
        <select
          value={priceKind}
          onChange={(e) => setPriceKind(e.target.value as typeof priceKind)}
          className="rounded-lg border border-brand-border px-2 py-1.5 text-sm"
        >
          <option value="fixed">Fixed price</option>
          <option value="hourly">Hourly rate</option>
          <option value="range">Price range</option>
        </select>
        {priceKind === 'range' ? (
          <>
            <input
              value={min}
              onChange={(e) => setMin(e.target.value)}
              placeholder="Min $"
              className="w-24 rounded-lg border border-brand-border px-2 py-1.5 text-sm"
            />
            <input
              value={max}
              onChange={(e) => setMax(e.target.value)}
              placeholder="Max $"
              className="w-24 rounded-lg border border-brand-border px-2 py-1.5 text-sm"
            />
          </>
        ) : (
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={priceKind === 'hourly' ? '$/hr' : 'Price $'}
            className="w-28 rounded-lg border border-brand-border px-2 py-1.5 text-sm"
          />
        )}
        <label className="flex items-center gap-1.5 text-sm text-brand-charcoal">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Visible to agents
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm text-brand-charcoal hover:bg-brand-surface">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving || !name.trim() || !description.trim()}
          className="rounded-lg bg-brand-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-accentHover disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function OfferingsTab() {
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await authedFetch('/profile/offerings', { method: 'POST' });
      if (!res.ok) throw new Error('Failed to load offerings.');
      const data = await res.json();
      setOfferings(data.offerings);
    } catch (err: any) {
      setError(err.message || 'Could not load your offerings.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (o: Omit<Offering, 'id'> & { id?: string }) => {
    const res = await authedFetch('/profile/offerings/save', {
      method: 'POST',
      body: JSON.stringify(o),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to save offering.');
    }
    setEditingId(null);
    await load();
  };

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading…</p>;

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}

      {offerings.length === 0 && editingId !== 'new' && (
        <div className="rounded-xl border border-dashed border-brand-border p-6 text-center">
          <p className="text-sm text-brand-gray mb-3">
            You haven't listed any services yet — agents can't see what you offer until you add one.
          </p>
          <button
            onClick={() => setEditingId('new')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-accent px-4 py-2 text-sm font-medium text-white hover:bg-brand-accentHover"
          >
            <PlusIcon className="size-4" /> Add your first offering
          </button>
        </div>
      )}

      {offerings.map((o) =>
        editingId === o.id ? (
          <OfferingEditor
            key={o.id}
            initial={o}
            onSave={handleSave}
            onCancel={() => setEditingId(null)}
          />
        ) : (
          <div key={o.id} className="flex items-start justify-between rounded-xl border border-brand-border bg-brand-white p-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-brand-black">{o.name}</span>
                {!o.active && (
                  <span className="rounded-full bg-brand-surface px-2 py-0.5 text-xs text-brand-gray">Hidden</span>
                )}
              </div>
              <p className="mt-1 text-sm text-brand-charcoal">{o.description}</p>
              <p className="mt-1 text-xs font-mono text-brand-gray">{formatPrice(o)} · {o.type}</p>
            </div>
            <button
              onClick={() => setEditingId(o.id)}
              className="shrink-0 rounded-lg px-3 py-1.5 text-sm text-brand-charcoal hover:bg-brand-surface"
            >
              Edit
            </button>
          </div>
        )
      )}

      {editingId === 'new' && (
        <OfferingEditor initial={{}} onSave={handleSave} onCancel={() => setEditingId(null)} />
      )}

      {offerings.length > 0 && editingId !== 'new' && (
        <button
          onClick={() => setEditingId('new')}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-4 py-2 text-sm text-brand-charcoal hover:bg-brand-surface"
        >
          <PlusIcon className="size-4" /> Add another offering
        </button>
      )}
    </div>
  );
}

// ---- How You Work tab -------------------------------------------------

function HowYouWorkTab() {
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [requirements, setRequirements] = useState<EngagementRequirements | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [minValue, setMinValue] = useState('');
  const [discoveryRequired, setDiscoveryRequired] = useState(false);
  const [briefRequired, setBriefRequired] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [capRes, reqRes] = await Promise.all([
          authedFetch('/profile/capabilities'),
          authedFetch('/profile/engagement-requirements'),
        ]);
        if (!capRes.ok || !reqRes.ok) throw new Error('Failed to load profile data.');
        const capData = await capRes.json();
        const reqData: EngagementRequirements = await reqRes.json();
        setCapabilities(capData.capabilities ?? []);
        setRequirements(reqData);
        setMinValue(reqData.minimumProjectValueCents != null ? String(reqData.minimumProjectValueCents / 100) : '');
        setDiscoveryRequired(reqData.discoveryCallRequired);
        setBriefRequired(reqData.requiresWrittenBrief);
      } catch (err: any) {
        setError(err.message || 'Could not load this data.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleSaveRequirements = async () => {
    setIsSaving(true);
    try {
      const res = await authedFetch('/profile/engagement-requirements/save', {
        method: 'POST',
        body: JSON.stringify({
          minimumProjectValueCents: minValue.trim() ? Math.round(parseFloat(minValue) * 100) : null,
          requiresWrittenBrief: briefRequired,
          discoveryCallRequired: discoveryRequired,
          minimumTimelineDays: requirements?.minimumTimelineDays ?? null,
        }),
      });
      if (!res.ok) throw new Error('Failed to save.');
    } catch (err: any) {
      setError(err.message || 'Could not save your requirements.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading…</p>;

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* ── Connected Services Section ── */}
      <div>
        <h3 className="text-sm font-medium text-brand-black mb-2">Connected Services</h3>
        <SearchConsoleCard />
      </div>

      <div>
        <h3 className="text-sm font-medium text-brand-black mb-2">Capabilities</h3>
        <div className="space-y-2">
          {capabilities.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-xl border border-brand-border bg-brand-white p-3">
              <div>
                <span className="text-sm font-medium text-brand-black">{c.label}</span>
                {c.pattern && <p className="text-xs text-brand-gray mt-0.5">{c.pattern}</p>}
                {c.fits_if && <p className="text-xs text-brand-gray/70 mt-0.5 italic">{c.fits_if}</p>}
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-mono ${
                  c.current_state === 'ACTIVE' ? 'bg-green-50 text-green-700' : 'bg-brand-surface text-brand-gray'
                }`}
              >
                {c.current_state === 'ACTIVE' ? 'Active' : 'Pending'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {requirements?.applicable && (
        <div>
          <h3 className="text-sm font-medium text-brand-black mb-2">Engagement requirements</h3>
          <div className="space-y-3 rounded-xl border border-brand-border bg-brand-white p-4">
            <label className="block text-sm text-brand-charcoal">
              Minimum project value ($)
              <input
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                placeholder="No minimum"
                className="mt-1 w-40 rounded-lg border border-brand-border px-3 py-1.5 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-brand-charcoal">
              <input type="checkbox" checked={discoveryRequired} onChange={(e) => setDiscoveryRequired(e.target.checked)} />
              Require a discovery call before proposals
            </label>
            <label className="flex items-center gap-2 text-sm text-brand-charcoal">
              <input type="checkbox" checked={briefRequired} onChange={(e) => setBriefRequired(e.target.checked)} />
              Require a written brief before contact
            </label>
            <button
              onClick={handleSaveRequirements}
              disabled={isSaving}
              className="rounded-lg bg-brand-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-accentHover disabled:opacity-50"
            >
              {isSaving ? <Loader2Icon className="inline size-4 animate-spin" /> : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- page ---------------------------------------------------------

export const DashboardProfile: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-brand-black">Your Profile</h1>
        <p className="text-sm text-brand-gray mt-1">
          What agents see when they look you up — your services, pricing, and how you work.
        </p>
      </div>

      <Tabs defaultValue="offerings" orientation="horizontal" className="flex-col!">
        <TabsList className="inline-flex! w-fit! flex-row!">
          <TabsTrigger value="offerings">Services &amp; Pricing</TabsTrigger>
          <TabsTrigger value="how-you-work">How You Work</TabsTrigger>
        </TabsList>
        <TabsContent value="offerings">
          <OfferingsTab />
        </TabsContent>
        <TabsContent value="how-you-work">
          <HowYouWorkTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};